import { z } from 'zod';

// Full canonical set — parity with the legacy V2 `LanguageCode` enum
// (travel_planner_back src/common/enum/language-code.ts): 23 codes. The
// itinerary Appearance → Language dropdown, product/T&C translation, and the
// preview localisation all draw from this list. Kept alphabetical.
export const languageCodeValues = [
  'al',
  'ar',
  'da',
  'de',
  'el',
  'en',
  'es',
  'fr',
  'hi',
  'id',
  'it',
  'ja',
  'ko',
  'nl',
  'no',
  'pl',
  'pt',
  'ru',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
] as const;

export const languageCodeSchema = z.enum(languageCodeValues);

export type LanguageCode = z.infer<typeof languageCodeSchema>;

export const facilityTranslationSchema = z.object({
  languageCode: languageCodeSchema,
  text: z.string(),
});

export type FacilityTranslation = z.infer<typeof facilityTranslationSchema>;

const facilityTranslationOutputSchema = z.object({
  languageCode: z.string(),
  text: z.string(),
});

export const facilitySchema = z.object({
  id: z.uuid(),
  title: z.array(facilityTranslationOutputSchema),
  createdOn: z.string(),
  /** Pool, garden, terrace…: a hotel's outdoors unless the hotel says not. */
  isOutdoor: z.boolean(),
});

export type Facility = z.infer<typeof facilitySchema>;

export const createFacilityInput = z.object({
  title: z.array(facilityTranslationSchema).optional(),
});

export type CreateFacilityInput = z.infer<typeof createFacilityInput>;

export const updateFacilityInput = z.object({
  id: z.uuid(),
  title: z.array(facilityTranslationSchema).optional(),
});

export type UpdateFacilityInput = z.infer<typeof updateFacilityInput>;

export const findFacilitiesInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(100),
  search: z.string().optional(),
  languageCodes: z.array(languageCodeSchema).optional(),
});

export type FindFacilitiesInput = z.infer<typeof findFacilitiesInput>;

export const findOneFacilityInput = z.object({
  id: z.string(),
  languages: z.array(languageCodeSchema).optional(),
});

export type FindOneFacilityInput = z.infer<typeof findOneFacilityInput>;

export const paginatedFacilitiesSchema = z.object({
  data: z.array(facilitySchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedFacilities = z.infer<typeof paginatedFacilitiesSchema>;
